import Code from '@/components/dashboard/Code';
import SectionActions from '@/docs/SectionActions';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function CSDKDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="AxionQuant C SDK" description="The AxionQuant C SDK (also called the Axion C SDK) provides a lightweight, high-performance C library for interacting with the AxionQuant Financial API.
          This SDK offers direct access to financial data including ESG scores, stoc..."  canonicalUrl="https://axionquant.com/docs/client-libraries/C-SDK" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">AxionQuant C SDK</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The AxionQuant C SDK (also called the Axion C SDK) provides a lightweight, high-performance C library for interacting with the AxionQuant Financial API.
          This SDK offers direct access to financial data including ESG scores, stock prices, cryptocurrency data,
          economic indicators, and more. Designed for embedded systems, high-frequency applications, and integration with C/C++ projects.
        </p>
        <p className="text-md text-gray-600 mt-2">
          GitHub: <a href="https://github.com/axionquant/c-sdk" className="text-blue-600 hover:underline">https://github.com/axionquant/c-sdk</a>
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Installation & Dependencies */}
        <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Installation & Dependencies</h2>
              <p className="text-gray-700 mb-4">
                The SDK requires <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">libcurl</code> for HTTP requests and
                <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded ml-1">cJSON</code> for JSON parsing.
                Both can be installed via system package managers or included as submodules.
              </p>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">System Dependencies</h3>
              <div className="space-y-2">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">Ubuntu/Debian</p>
                  <p className="text-sm text-gray-600">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">sudo apt-get install libcurl4-openssl-dev libcjson-dev</code>
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">macOS (Homebrew)</p>
                  <p className="text-sm text-gray-600">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">brew install curl cjson</code>
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Building from Source</h3>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="text-sm text-gray-800 mb-1">Clone and compile</p>
                <p className="text-sm text-gray-600">
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">git clone https://github.com/axionquant/c-sdk.git</code><br />
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">cd c-sdk && mkdir build && cd build</code><br />
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">cmake .. && make</code><br />
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">sudo make install   # optional</code>
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Compiler Flags" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Compiler Flags</h3>
              <p className="text-gray-400 text-xs mb-2">Linker flags</p>
              <Code lang="bash">{`# Static linking
-laxion -lcurl -lcjson

# Or pkg-config (if installed)
pkg-config --cflags --libs axion`}</Code>
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Quick Start</h2>
              <p className="text-gray-700 mb-4">
                A minimal example that initializes the client, fetches stock data and ESG scores for Apple, and cleans up.
                Always check for errors and free responses.
              </p>
            </div>
            <div className="mb-6">
              <div className="border-l-4 border-blue-500 pl-4 mb-3">
                <p className="font-mono text-sm text-gray-800 mb-1">Steps</p>
                <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
                  <li>Include the main header</li>
                  <li>Initialize client with your API key</li>
                  <li>Make API calls</li>
                  <li>Process JSON responses using cJSON</li>
                  <li>Free responses and client</li>
                </ol>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Quick Start (C)" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Quick Start (C)</p>
              <Code lang="c">{`#include "axion.h"
#include <stdio.h>

int main() {
    AxionClient* client = axion_init("your_api_key_here");
    if (!client) return 1;

    // Get stock quote for Apple
    AxionResponse* quote = axion_stocks_ticker(client, "AAPL");
    if (quote && quote->json && !quote->error) {
        cJSON* price = cJSON_GetObjectItem(quote->json, "price");
        if (cJSON_IsNumber(price))
            printf("AAPL Price: %.2f\\n", price->valuedouble);
    }
    axion_response(quote);

    // Get ESG data
    AxionResponse* esg = axion_esg_data(client, "AAPL");
    if (esg && esg->json && !esg->error) {
        cJSON* score = cJSON_GetObjectItem(esg->json, "score");
        if (cJSON_IsNumber(score))
            printf("ESG Score: %.2f\\n", score->valuedouble);
    }
    axion_response(esg);

    axion_client(client);
    return 0;
}`}</Code>
            </div>
          </div>
        </div>

        {/* Client Initialization */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Client Initialization</h2>
              <p className="text-gray-700 mb-6">
                The SDK follows a traditional C pattern: explicit initialization, manual cleanup, and response structures
                that must be freed. All API calls return an <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">AxionResponse*</code>.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Core Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Returns</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_init(api_key)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Create client, init curl</td>
                      <td className="px-4 py-3 text-sm"><span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">AxionClient*</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_client(client)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Cleanup client, global curl</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs bg-gray-100 px-2 py-1 rounded">void</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_response(resp)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Free response + JSON</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs bg-gray-100 px-2 py-1 rounded">void</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Structure</h3>
              <div className="space-y-2">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">AxionResponse</p>
                  <p className="text-xs text-gray-600 mb-1">
                    <span className="font-mono">int http_status;</span> - HTTP status code<br />
                    <span className="font-mono">char *data;</span> - raw JSON string<br />
                    <span className="font-mono">cJSON *json;</span> - parsed JSON (NULL if invalid)<br />
                    <span className="font-mono">char *error;</span> - error message (NULL if none)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Error-aware response handling" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Error-aware response handling</p>
              <Code lang="c">{`AxionResponse* resp = axion_esg_data(client, "AAPL");
if (!resp) {
    // fatal allocation error
    return;
}

if (resp->error) {
    fprintf(stderr, "API error: %s\\n", resp->error);
} else if (resp->http_status >= 400) {
    fprintf(stderr, "HTTP %d error\\n", resp->http_status);
} else if (!resp->json) {
    fprintf(stderr, "JSON parse failed\\n");
} else {
    // success, use resp->json
    cJSON* score = cJSON_GetObjectItem(resp->json, "score");
    // process...
}

axion_response(resp);`}</Code>
            </div>
          </div>
        </div>

        {/* API Categories Overview */}
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">API Categories</h2>
            <p className="text-gray-700 max-w-[900px]">
              The SDK provides comprehensive coverage of financial data through categorized API functions.
              Each category follows consistent naming patterns for easy discovery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1400px]">
            <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Stocks</h4>
              <p className="text-sm text-blue-800 mb-2">Tickers, quotes, prices, gainers/losers, listings</p>
              <div className="space-y-1">
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_tickers()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_ticker()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_prices()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_gainers()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_losers()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_quote()</code>
                <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_stocks_list_*()</code>
              </div>
            </div>
            <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">Crypto</h4>
              <p className="text-sm text-purple-800 mb-2">Cryptocurrency data</p>
              <div className="space-y-1">
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_tickers()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_ticker()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_prices()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_gainers()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_losers()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_quote()</code>
                <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_crypto_list_*()</code>
              </div>
            </div>
            <div className="bg-green-50 p-5 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Forex</h4>
              <p className="text-sm text-green-800 mb-2">Currency pairs</p>
              <div className="space-y-1">
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_tickers()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_ticker()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_prices()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_gainers()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_losers()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_quote()</code>
                <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_forex_list_*()</code>
              </div>
            </div>
            <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">Futures</h4>
              <p className="text-sm text-yellow-800 mb-2">Futures contracts</p>
              <div className="space-y-1">
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_tickers()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_ticker()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_prices()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_gainers()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_losers()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_quote()</code>
                <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_futures_list_*()</code>
              </div>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2">Indices</h4>
              <p className="text-sm text-red-800 mb-2">Market indices</p>
              <div className="space-y-1">
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_tickers()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_ticker()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_prices()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_gainers()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_losers()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_quote()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_components()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_exposure()</code>
                <code className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_indices_list_*()</code>
              </div>
            </div>
            <div className="bg-pink-50 p-5 rounded-lg border border-pink-200">
              <h4 className="font-semibold text-pink-900 mb-2">Profiles</h4>
              <p className="text-sm text-pink-800 mb-2">Company information</p>
              <div className="space-y-1">
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_profile()</code>
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_info()</code>
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_summary()</code>
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_statistics()</code>
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_recommendation()</code>
                <code className="text-xs text-pink-700 bg-pink-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_profiles_calendar()</code>
              </div>
            </div>
            <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-900 mb-2">Financials</h4>
              <p className="text-sm text-indigo-800 mb-2">Income, balance sheet, cash flow, metrics</p>
              <div className="space-y-1">
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_revenue()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_net_income()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_metrics()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_snapshot()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_eps()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_pe()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_market_cap()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_roe()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_enterprise_value()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_ebitda()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_debt_to_equity()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_balance_sheet()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_income_statement()</code>
                <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_financials_cash_flow_statement()</code>
              </div>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border border-teal-200">
              <h4 className="font-semibold text-teal-900 mb-2">Earnings</h4>
              <p className="text-sm text-teal-800 mb-2">Earnings history, trends, reports, transcripts</p>
              <div className="space-y-1">
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_history()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_trend()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_index()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_report()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_transcript()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_earnings_transcript_sentiment()</code>
              </div>
            </div>
            <div className="bg-orange-50 p-5 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-900 mb-2">Filings</h4>
              <p className="text-sm text-orange-800 mb-2">SEC filings, forms, search, documents</p>
              <div className="space-y-1">
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_recent()</code>
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_history()</code>
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_list_forms()</code>
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_search()</code>
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_document_text()</code>
                <code className="text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_filings_document_sentiment()</code>
              </div>
            </div>
            <div className="bg-cyan-50 p-5 rounded-lg border border-cyan-200">
              <h4 className="font-semibold text-cyan-900 mb-2">Insiders</h4>
              <p className="text-sm text-cyan-800 mb-2">Insider trading, ownership, funds</p>
              <div className="space-y-1">
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_funds()</code>
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_individuals()</code>
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_institutions()</code>
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_ownership()</code>
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_activity()</code>
                <code className="text-xs text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_insiders_transactions()</code>
              </div>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-900 mb-2">Sentiment</h4>
              <p className="text-sm text-amber-800 mb-2">Social, news, analyst sentiment</p>
              <div className="space-y-1">
                <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_sentiment_social()</code>
                <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_sentiment_news()</code>
                <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_sentiment_analyst()</code>
              </div>
            </div>
            <div className="bg-lime-50 p-5 rounded-lg border border-lime-200">
              <h4 className="font-semibold text-lime-900 mb-2">Economic</h4>
              <p className="text-sm text-lime-800 mb-2">Indicators, calendar, datasets, search</p>
              <div className="space-y-1">
                <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_econ_search()</code>
                <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_econ_find()</code>
                <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_econ_dataset()</code>
                <code className="text-xs text-lime-700 bg-lime-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_econ_calendar()</code>
              </div>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border border-teal-200">
              <h4 className="font-semibold text-teal-900 mb-2">ETF</h4>
              <p className="text-sm text-teal-800 mb-2">ETF data, holdings, exposure, weights</p>
              <div className="space-y-1">
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_tickers()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_ticker()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_prices()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_fund()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_holdings()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_holdings_all()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_exposure()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_weights()</code>
                <code className="text-xs text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_etfs_quote()</code>
              </div>
            </div>
          </div>
        </div>

        {/* ETF API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">ETF API</h2>
              <p className="text-gray-700 mb-6">
                Comprehensive ETF data including fund information, holdings, exposure, weights, gainers, and losers.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Filtered ETF list</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">country, exchange</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current ETF quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_fund()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Fund overview and details</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_holdings()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top holdings</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_holdings_all()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">All holdings</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_exposure()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Sector and asset exposure</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_weights()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Weight allocations</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time ETF quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_gainers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top gaining ETFs</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_losers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top losing ETFs</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_market()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available markets</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_country()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available countries</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_currency()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available currencies</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_sector()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available sectors</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_industry()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available industries</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_etfs_list_type()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available types</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ETF Examples" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">ETF Examples</p>
              <Code lang="c">{`// Get SPY ETF holdings
AxionResponse* holdings = axion_etfs_holdings(client, "SPY");
if (holdings && holdings->json) {
    printf("SPY holdings retrieved\\n");
}
axion_response(holdings);

// Get all SPY ETF holdings
AxionResponse* allHoldings = axion_etfs_holdings_all(client, "SPY");
if (allHoldings && allHoldings->json) {
    printf("SPY all holdings retrieved\\n");
}
axion_response(allHoldings);

// Get top gaining ETFs
AxionResponse* gainers = axion_etfs_gainers(client, 7, 10);
if (gainers && gainers->json) {
    printf("Top ETF gainers retrieved\\n");
}
axion_response(gainers);

// Get ETF exposure
AxionResponse* exposure = axion_etfs_exposure(client, "QQQ");
if (exposure && exposure->json) {
    printf("QQQ exposure data retrieved\\n");
}
axion_response(exposure);`}</Code>
            </div>
          </div>
        </div>

        {/* Market Data APIs (Stocks, Crypto, Forex, Futures, Indices) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Market Data APIs</h2>
              <p className="text-gray-700 mb-6">
                All market data endpoints follow consistent patterns: tickers lists, individual ticker quotes, historical prices,
                gainers/losers, and metadata listings.
              </p>
            </div>

            {/* Stocks Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Stocks API</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Filtered ticker list</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">country, exchange</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current quote for a ticker</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_gainers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top gainers</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit, market</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_losers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top losers</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit, market</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time stock quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_market()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available markets</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_country()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available countries</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_currency()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available currencies</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_sector()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available sectors</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_industry()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available industries</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_stocks_list_type()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available types</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Crypto Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Cryptocurrency API</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of crypto tickers</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">type (optional)</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current quote (e.g., "BTC-USD")</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical prices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_gainers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top gaining cryptocurrencies</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_losers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top losing cryptocurrencies</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time crypto quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_list_category()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available categories</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_list_rating()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available ratings</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_crypto_list_type()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available types</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Forex Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Forex API</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Filtered forex tickers</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">country, exchange</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current forex quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical forex prices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_gainers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top gaining currencies</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_losers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top losing currencies</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time forex quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_list_exchange()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available exchanges</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_list_rating()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available ratings</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_forex_list_country()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available countries</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Futures Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Futures API</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Filtered index list</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">exchange</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current index value</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical index data</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_gainers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top gaining indices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_losers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Top losing indices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time index quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_list_exchange()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available exchanges</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_indices_list_type()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available types</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Indices Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Indices API</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_tickers()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">exchange</code></td>
                      <td className="px-4 py-3 text-sm">Filtered indices list</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_ticker()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Current index quote</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_prices()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                      <td className="px-4 py-3 text-sm">Historical index prices</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_gainers()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                      <td className="px-4 py-3 text-sm">Top gaining indices</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_losers()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">days, limit</code></td>
                      <td className="px-4 py-3 text-sm">Top losing indices</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_quote()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Real-time index quote</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_components()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Index constituent list</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_exposure()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Index sector exposure</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_list_exchange()</td>
                      <td className="px-4 py-3 text-sm">None</td>
                      <td className="px-4 py-3 text-sm">Available exchanges</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_list_timezone()</td>
                      <td className="px-4 py-3 text-sm">None</td>
                      <td className="px-4 py-3 text-sm">Available timezones</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_indices_list_country()</td>
                      <td className="px-4 py-3 text-sm">None</td>
                      <td className="px-4 py-3 text-sm">Available countries</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Market Data Examples" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Market Data Examples</p>
              <Code lang="c">{`// Get NASDAQ stocks
AxionResponse* tickers = axion_stocks_tickers(client, "US", "NASDAQ");

// Get Bitcoin quote
AxionResponse* btc = axion_crypto_ticker(client, "BTC-USD");

// Get historical prices for EUR/USD
AxionResponse* eur = axion_forex_prices(client, "EUR-USD",
    "2024-01-01", "2024-03-31", "daily");

// Get S&P 500 futures
AxionResponse* sp = axion_futures_ticker(client, "ES");

// Get top stock gainers
AxionResponse* gainers = axion_stocks_gainers(client, 7, 10, "US");

// Get S&P 500 components
AxionResponse* sp500 = axion_indices_components(client, "SPX");`}</Code>
            </div>
          </div>
        </div>

        {/* Profiles API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Profiles API</h2>
              <p className="text-gray-700 mb-6">
                Comprehensive company data and metadata.
              </p>
            </div>
            <div className="mb-8">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_profile()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Basic company profile</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_info()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Detailed company information</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_summary()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Financial summary</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_statistics()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Key statistics and ratios</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_recommendation()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Analyst recommendations</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_profiles_calendar()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                      <td className="px-4 py-3 text-sm">Upcoming events calendar</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Profiles Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Profiles Example</p>
              <Code lang="c">{`AxionResponse* info = axion_profiles_info(client, "AAPL");
if (info && info->json) {
    cJSON* name = cJSON_GetObjectItem(info->json, "name");
    cJSON* sector = cJSON_GetObjectItem(info->json, "sector");
    printf("Company: %s\\nSector: %s\\n", 
           name->valuestring, sector->valuestring);
}
axion_response(info);

AxionResponse* recs = axion_profiles_recommendation(client, "AAPL");
// Process recommendations...
axion_response(recs);`}</Code>
            </div>
          </div>
        </div>

        {/* Financials API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Financials API</h2>
              <p className="text-gray-700 mb-6">
                Detailed financial statements and metrics. Most functions accept an optional <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">periods</code> parameter to specify number of historical periods.
                Statement functions accept <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">year</code> and <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">quarter</code> parameters.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_revenue(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Revenue data</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_net_income(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Net income</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_total_assets(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Total assets</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_total_liabilities(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Total liabilities</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_stockholders_equity(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stockholders equity</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_current_assets(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current assets</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_current_liabilities(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current liabilities</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_operating_cash_flow(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Operating cash flow</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_capital_expenditures(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">CapEx</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_free_cash_flow(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Free cash flow</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_shares_outstanding_basic(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Basic shares outstanding</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_shares_outstanding_diluted(ticker, periods)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Diluted shares outstanding</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, periods</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_metrics(ticker)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Calculated financial metrics</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_snapshot(ticker)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Financial snapshot overview</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_eps(ticker, from, to)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Earnings per share</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_pe(ticker, from, to, frame)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">P/E ratio</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_market_cap(ticker, from, to, frame)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Market capitalization</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_roe(ticker, from, to)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Return on equity</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_enterprise_value(ticker, from, to, frame)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Enterprise value</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_ebitda(ticker, from, to)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">EBITDA</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_debt_to_equity(ticker, from, to)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Debt-to-equity ratio</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_balance_sheet(client, ticker, year, quarter)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Complete balance sheet statement</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">client, ticker, year, quarter</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_income_statement(client, ticker, year, quarter)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Complete income statement</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">client, ticker, year, quarter</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_financials_cash_flow_statement(client, ticker, year, quarter)</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Complete cash flow statement</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">client, ticker, year, quarter</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Financials Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Financials Example</p>
              <Code lang="c">{`// Get last 4 quarters of revenue
AxionResponse* revenue = axion_financials_revenue(client, "AAPL", 4);

// Get current metrics
AxionResponse* metrics = axion_financials_metrics(client, "AAPL");

if (metrics && metrics->json) {
    cJSON* pe = cJSON_GetObjectItem(metrics->json, "pe_ratio");
    cJSON* pb = cJSON_GetObjectItem(metrics->json, "pb_ratio");
    printf("P/E: %.2f, P/B: %.2f\\n", 
           pe->valuedouble, pb->valuedouble);
}

// Get balance sheet for Q1 2024
AxionResponse* bs = axion_financials_balance_sheet(client, "AAPL", "2024", "Q1");
if (bs && bs->json) {
    printf("Balance sheet retrieved\\n");
}
axion_response(bs);

axion_response(revenue);
axion_response(metrics);`}</Code>
            </div>
          </div>
        </div>

        {/* Earnings API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Earnings API</h2>
              <p className="text-gray-700 mb-6">
                Comprehensive earnings data including history, trends, detailed reports, and call transcripts.
              </p>
            </div>
            <div className="mb-8">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_tickers()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Filtered futures list</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">type, exchange</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_ticker()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current futures quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_prices()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Historical futures prices</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, from, to, frame</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_quote()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Real-time futures quote</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_list_exchange()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available exchanges</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_futures_list_type()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Available types</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Earnings Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Earnings Example</p>
              <Code lang="c">{`// Get earnings history
AxionResponse* history = axion_earnings_history(client, "AAPL");

// Get Q1 2024 earnings report
AxionResponse* report = axion_earnings_report(client, "AAPL", "2024", "Q1");

if (report && report->json) {
    cJSON* eps = cJSON_GetObjectItem(report->json, "eps_actual");
    cJSON* revenue = cJSON_GetObjectItem(report->json, "revenue_actual");
    printf("EPS: %.2f, Revenue: %.2f\\n", 
           eps->valuedouble, revenue->valuedouble);
}

// Get earnings call transcript
AxionResponse* transcript = axion_earnings_transcript(client, "AAPL", "2024", "Q1");
if (transcript && transcript->json) {
    printf("Transcript retrieved\\n");
}
axion_response(transcript);

axion_response(history);
axion_response(report);`}</Code>
            </div>
          </div>
        </div>

        {/* Filings API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Filings API</h2>
              <p className="text-gray-700 mb-6">
                SEC filings data with powerful search capabilities and document access.
              </p>
            </div>
            <div className="mb-8">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_recent()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, form, limit</code></td>
                      <td className="px-4 py-3 text-sm">List of filings for a ticker</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_history()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, form_type, start_date, end_date</code></td>
                      <td className="px-4 py-3 text-sm">Filtered forms for a ticker by date range</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_list_forms()</td>
                      <td className="px-4 py-3 text-sm">None</td>
                      <td className="px-4 py-3 text-sm">Description of all form types</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_search()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker, form, year, quarter</code></td>
                      <td className="px-4 py-3 text-sm">Search across all filings</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_document_text()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">document_id</code></td>
                      <td className="px-4 py-3 text-sm">Full document text content</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono">axion_filings_document_sentiment()</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">document_id</code></td>
                      <td className="px-4 py-3 text-sm">Document sentiment analysis</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Filings Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Filings Example</p>
              <Code lang="c">{`// Get latest 10-K filings for Apple
AxionResponse* filings = axion_filings_recent(client, "AAPL", "10-K", 5);

// Search for all 10-Q filings in Q1 2024
AxionResponse* search = axion_filings_search(client, NULL, "10-Q", "2024", "Q1");

if (search && search->json) {
    cJSON* filings_array = search->json;
    int count = cJSON_GetArraySize(filings_array);
    printf("Found %d filings\\n", count);
}

// Get document text
AxionResponse* doc = axion_filings_document_text(client, "doc_12345");
if (doc && doc->json) {
    printf("Document text retrieved\\n");
}
axion_response(doc);

axion_response(filings);
axion_response(search);`}</Code>
            </div>
          </div>
        </div>

        {/* Insiders API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Insiders API</h2>
              <p className="text-gray-700 mb-6">
                Insider trading data including transactions, ownership, funds, and activity.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_funds()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Fund ownership data</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_individuals()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Insider holders (individuals)</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_institutions()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Institutional ownership</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_ownership()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Major holders breakdown</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_activity()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Net share purchase activity</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_insiders_transactions()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Insider transactions</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Insiders Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Insiders Example</p>
              <Code lang="c">{`// Get recent insider transactions
AxionResponse* transactions = axion_insiders_transactions(client, "AAPL");

if (transactions && transactions->json) {
    cJSON* first = cJSON_GetArrayItem(transactions->json, 0);
    if (first) {
        cJSON* name = cJSON_GetObjectItem(first, "insider_name");
        cJSON* shares = cJSON_GetObjectItem(first, "shares");
        printf("%s traded %d shares\\n", 
               name->valuestring, shares->valueint);
    }
}
axion_response(transactions);`}</Code>
            </div>
          </div>
        </div>

        {/* Sentiment API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Sentiment API</h2>
              <p className="text-gray-700 mb-6">
                Multi-source sentiment analysis for stocks.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_sentiment_social()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Social media sentiment</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_sentiment_news()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">News-based sentiment</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_sentiment_analyst()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Analyst sentiment</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Sentiment Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Sentiment Example</p>
              <Code lang="c">{`AxionResponse* sentiment = axion_sentiment_social(client, "TSLA");
if (sentiment && sentiment->json) {
    cJSON* label = cJSON_GetObjectItem(sentiment->json, "label");
    cJSON* score = cJSON_GetObjectItem(sentiment->json, "score");

    printf("Label: %s, Score: %.2f\\n",
           label && label->valuestring ? label->valuestring : "N/A",
           score ? score->valuedouble : 0.0);
}
axion_response(sentiment);`}</Code>
            </div>
          </div>
        </div>

        {/* News API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">News API</h2>
              <p className="text-gray-700 mb-6">
                Financial news from multiple sources.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_news_general()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">General market news feed</td>
                      <td className="px-4 py-3 text-sm"><span className="text-xs text-gray-500">None</span></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_news_company()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">News for a specific company</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">ticker</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_news_country()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">News by country</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">country</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_news_category()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">News by category</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">category</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="News Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">News Example</p>
              <Code lang="c">{`// Get latest news for Apple
AxionResponse* news = axion_news_company(client, "AAPL");
if (news && news->json) {
    cJSON* first = cJSON_GetArrayItem(news->json, 0);
    if (first) {
        cJSON* title = cJSON_GetObjectItem(first, "title");
        cJSON* source = cJSON_GetObjectItem(first, "source");
        printf("%s - %s\\n", source->valuestring, title->valuestring);
    }
}
axion_response(news);`}</Code>
            </div>
          </div>
        </div>

        {/* Economic API */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Economic API</h2>
              <p className="text-gray-700 mb-6">
                Macroeconomic indicators, datasets, natural language search, and economic calendar.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Functions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Function</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameters</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_econ_search()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Search for economic series</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">query</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_econ_find()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">FRED series search</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">query</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_econ_dataset()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Get series observations</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">series_id</code></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">axion_econ_calendar()</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Economic calendar with filters</td>
                      <td className="px-4 py-3 text-sm"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">from, to, country, min_importance, currency, category</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Economic Example" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Economic Example</p>
              <Code lang="c">{`// Get economic calendar for US with min importance 3
AxionResponse* calendar = axion_econ_calendar(client, 
    "2024-03-01", "2024-03-31", "US", 3, NULL, NULL);

// Get GDP data
AxionResponse* gdp = axion_econ_dataset(client, "GDP");

// Economic search
AxionResponse* found = axion_econ_find(client, "US GDP growth rate 2024");
if (found && found->json) {
    printf("Economic data found\\n");
}
axion_response(found);

axion_response(calendar);
axion_response(gdp);`}</Code>
            </div>
          </div>
        </div>

        {/* ESG, Credit, Supply Chain, Web Traffic */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1400px]">
          {/* ESG API */}
          <div className="flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">ESG API</h2>
            <div className="bg-green-50 p-5 rounded-lg border border-green-200 mb-4">
              <p className="text-sm text-green-800 mb-2">Environmental, Social, Governance data</p>
              <code className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block mb-2">axion_esg_data(client, ticker)</code>
              <p className="text-xs text-green-600">Returns ESG scores and metrics for a company</p>
            </div>
          </div>

          {/* Credit API */}
          <div className="flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Credit API</h2>
            <div className="bg-blue-50 p-5 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-blue-800 mb-2">Credit ratings and entity search</p>
              <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block mb-1">axion_credit_search(client, query)</code>
              <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_credit_ratings(client, entity_id)</code>
            </div>
          </div>

          {/* Supply Chain API */}
          <div className="flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Supply Chain API</h2>
            <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200 mb-4">
              <p className="text-sm text-yellow-800 mb-2">Customers, peers, suppliers</p>
              <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block mb-1">axion_supply_chain_customers(client, ticker)</code>
              <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block mb-1">axion_supply_chain_peers(client, ticker)</code>
              <code className="text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_supply_chain_suppliers(client, ticker)</code>
            </div>
          </div>

          {/* Web Traffic API */}
          <div className="flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Web Traffic API</h2>
            <div className="bg-purple-50 p-5 rounded-lg border border-purple-200 mb-4">
              <p className="text-sm text-purple-800 mb-2">Website traffic metrics</p>
              <code className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap overflow-x-auto block">axion_webtraffic_traffic(client, ticker)</code>
            </div>
          </div>
        </div>

        {/* Error Handling */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Error Handling</h2>
              <p className="text-gray-700 mb-6">
                Every API call returns an <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">AxionResponse*</code> that must be checked for errors.
              </p>
            </div>
            <div className="mb-8">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead><tr className="bg-gray-50"><th className="px-4 py-3 text-left text-sm font-semibold">Check</th><th className="px-4 py-3 text-left text-sm font-semibold">Description</th></tr></thead>
                  <tbody>
                    <tr><td className="px-4 py-3 text-sm font-mono">!response</td><td className="px-4 py-3 text-sm">Fatal error (allocation failure)</td></tr>
                    <tr><td className="px-4 py-3 text-sm font-mono">response-&gt;error</td><td className="px-4 py-3 text-sm">API error message</td></tr>
                    <tr><td className="px-4 py-3 text-sm font-mono">response-&gt;http_status &gt;= 400</td><td className="px-4 py-3 text-sm">HTTP error</td></tr>
                    <tr><td className="px-4 py-3 text-sm font-mono">!response-&gt;json</td><td className="px-4 py-3 text-sm">JSON parse failure</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Robust error handling" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Robust error handling</p>
              <Code lang="c">{`AxionResponse* resp = axion_esg_data(client, "INVALID");
if (!resp) {
    fprintf(stderr, "Fatal: allocation failed\\n");
    return;
}

if (resp->error) {
    fprintf(stderr, "API error: %s\\n", resp->error);
} else if (resp->http_status >= 400) {
    fprintf(stderr, "HTTP %d error\\n", resp->http_status);
} else if (!resp->json) {
    fprintf(stderr, "Invalid JSON response\\n");
} else {
    // Success - process resp->json
    cJSON* score = cJSON_GetObjectItem(resp->json, "score");
    // ...
}

axion_response(resp);`}</Code>
            </div>
          </div>
        </div>

        {/* Memory Management */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Memory Management</h2>
              <p className="text-gray-700 mb-6">
                The SDK uses manual memory management. Every <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">AxionResponse*</code> must be freed with
                <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded ml-1">axion_response()</code>. The client must be freed last.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-medium text-red-900 mb-1">Always free responses</h4>
                <p className="text-xs text-red-800">Use <code className="text-xs bg-red-100 px-1 py-0.5 rounded">axion_response()</code> for every response.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-yellow-900 mb-1">Free client last</h4>
                <p className="text-xs text-yellow-800">All responses freed before <code className="text-xs bg-yellow-100 px-1 py-0.5 rounded">axion_client()</code>.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-900 mb-1">NULL safe</h4>
                <p className="text-xs text-green-800">Free functions accept NULL.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-1">Thread safety</h4>
                <p className="text-xs text-blue-800">One client per thread, or use mutex.</p>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Cleanup with goto pattern" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Cleanup with goto pattern</p>
              <Code lang="c">{`AxionClient* client = NULL;
AxionResponse* r1 = NULL;
AxionResponse* r2 = NULL;

client = axion_init("your-api-key");
if (!client) goto cleanup;

r1 = axion_stocks_ticker(client, "AAPL");
if (!r1 || r1->error) goto cleanup;

r2 = axion_esg_data(client, "AAPL");
if (!r2 || r2->error) goto cleanup;

// Process responses...

cleanup:
    axion_response(r1);
    axion_response(r2);
    axion_client(client);`}</Code>
            </div>
          </div>
        </div>

        {/* Performance Tips */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Performance Tips</h2>
              <p className="text-gray-700 mb-6">
                Optimize for high-frequency or embedded environments.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium text-gray-900 mb-1">Reuse client</p>
                <p className="text-xs text-gray-600">One client instance per thread reduces curl init overhead.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium text-gray-900 mb-1">Batch calls</p>
                <p className="text-xs text-gray-600">Group API calls to reuse connections.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium text-gray-900 mb-1">Free early</p>
                <p className="text-xs text-gray-600">Free responses immediately after processing.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-medium text-gray-900 mb-1">Date formats</p>
                <p className="text-xs text-gray-600">Always use YYYY-MM-DD format for dates.</p>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Batch processing pattern" contentRef={sectionRef} />
              <p className="text-gray-400 text-xs mb-2">Batch processing pattern</p>
              <Code lang="c">{`// Fire all requests first
const char* tickers[] = {"AAPL", "MSFT", "GOOGL"};
AxionResponse* resps[3];

for (int i = 0; i < 3; i++) {
    resps[i] = axion_stocks_ticker(client, tickers[i]);
}

// Then process and free
for (int i = 0; i < 3; i++) {
    if (resps[i] && resps[i]->json && !resps[i]->error) {
        cJSON* price = cJSON_GetObjectItem(resps[i]->json, "price");
        printf("%s: %.2f\\n", tickers[i], price->valuedouble);
    }
    axion_response(resps[i]);
}`}</Code>
            </div>
          </div>
        </div>

        {/* Complete Example */}
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Complete Example</h2>
            <p className="text-gray-700 max-w-[900px] mb-6">
              A comprehensive program that fetches stock data, ESG scores, and financial metrics for multiple companies.
            </p>
          </div>
          <Code lang="c">{`#include "axion.h"
#include <stdio.h>
#include <string.h>

typedef struct {
    char ticker[16];
    double price;
    double esg_score;
    double pe_ratio;
    double market_cap;
} CompanyAnalysis;

int main() {
    AxionClient* client = axion_init("your-api-key-here");
    if (!client) {
        fprintf(stderr, "Failed to initialize client\\n");
        return 1;
    }

    const char* tickers[] = {"AAPL", "MSFT", "GOOGL", "TSLA", "AMZN"};
    CompanyAnalysis results[5] = {0};
    
    for (int i = 0; i < 5; i++) {
        strcpy(results[i].ticker, tickers[i]);
        
        // Get stock quote
        AxionResponse* quote = axion_stocks_ticker(client, tickers[i]);
        if (quote && quote->json && !quote->error) {
            cJSON* price = cJSON_GetObjectItem(quote->json, "price");
            if (cJSON_IsNumber(price)) {
                results[i].price = price->valuedouble;
            }
        }
        axion_response(quote);
        
        // Get ESG data
        AxionResponse* esg = axion_esg_data(client, tickers[i]);
        if (esg && esg->json && !esg->error) {
            cJSON* score = cJSON_GetObjectItem(esg->json, "score");
            if (cJSON_IsNumber(score)) {
                results[i].esg_score = score->valuedouble;
            }
        }
        axion_response(esg);
        
        // Get financial metrics
        AxionResponse* metrics = axion_financials_metrics(client, tickers[i]);
        if (metrics && metrics->json && !metrics->error) {
            cJSON* pe = cJSON_GetObjectItem(metrics->json, "pe_ratio");
            cJSON* mcap = cJSON_GetObjectItem(metrics->json, "market_cap");
            if (cJSON_IsNumber(pe)) results[i].pe_ratio = pe->valuedouble;
            if (cJSON_IsNumber(mcap)) results[i].market_cap = mcap->valuedouble;
        }
        axion_response(metrics);
    }
    
    // Print results
    printf("\\n=== Company Analysis ===\\n\\n");
    for (int i = 0; i < 5; i++) {
        printf("%s:\\n", results[i].ticker);
        printf("  Price: $%.2f\\n", results[i].price);
        printf("  ESG Score: %.1f\\n", results[i].esg_score);
        printf("  P/E Ratio: %.2f\\n", results[i].pe_ratio);
        printf("  Market Cap: $%.2fB\\n\\n", results[i].market_cap / 1e9);
    }
    
    axion_client(client);
    return 0;
}`}</Code>
        </div>

      </div>
    </div>
    </div>
  );
}
